/**
 * AdminOracleCertification — Retrieval Certification operational health panel.
 *
 * Nightly automated quality gate proving Oracle can retrieve every published
 * record. Shows the latest report, benchmark (sentinel) status, comparison
 * against the previous run, historical trend, and full failure drill-down
 * with direct links to affected records.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  ShieldCheck, RefreshCw, Loader2, AlertTriangle, CheckCircle2, XCircle,
  TrendingUp, TrendingDown, Minus, ExternalLink, ChevronDown, ChevronRight,
  Crosshair, Clock,
} from "lucide-react";
import { toast } from "sonner";
import { adminCertLatest, adminCertReports, adminCertRun } from "../../lib/api";

const HEALTH_COLOR = { PASS: "#3FB950", WARNING: "#E0A526", FAIL: "#D92D20", ERROR: "#D92D20" };

const _fmt = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }); }
  catch { return iso; }
};

const RECORD_PATH = {
  dossier: (s) => `/topic/${s}`,
  profiles: (s) => `/people/${s}`,
  organizations: (s) => `/organizations/${s}`,
  operations: (s) => `/operations/${s}`,
};

const Chip = ({ label, value, color, testid }) => (
  <div className="border border-[#2D2A26] px-3 py-2 text-center" data-testid={testid}>
    <div className="font-display text-xl" style={{ color: color || "#E4DFD3" }}>{value}</div>
    <div className="font-mono text-[8px] uppercase tracking-[0.2em] text-[#7B7168] mt-0.5">{label}</div>
  </div>
);

const Delta = ({ value, invert = false, suffix = "" }) => {
  if (value === null || value === undefined) return <span className="text-[#7B7168]">—</span>;
  const good = invert ? value < 0 : value > 0;
  const Icon = value === 0 ? Minus : (value > 0 ? TrendingUp : TrendingDown);
  const color = value === 0 ? "#7B7168" : good ? "#3FB950" : "#D92D20";
  return (
    <span className="inline-flex items-center gap-1 font-mono text-[11px]" style={{ color }}>
      <Icon size={11} />{value > 0 ? "+" : ""}{value}{suffix}
    </span>
  );
};

/* ---------- Trend chart (SVG, no deps) ---------- */
const TrendChart = ({ reports }) => {
  const pts = useMemo(() => (reports || [])
    .filter((r) => r.health !== "ERROR" && typeof r.overall_score === "number")
    .slice().reverse(), [reports]);
  if (pts.length < 2) {
    return <div className="font-mono text-[10px] text-[#7B7168] py-6 text-center"
                data-testid="cert-trend-empty">
      Not enough runs yet — the trend appears after two or more certifications.
    </div>;
  }
  const W = 640, H = 120, PAD = 8;
  const min = Math.max(0, Math.min(...pts.map((p) => p.overall_score)) - 5);
  const xs = (i) => PAD + (i * (W - 2 * PAD)) / (pts.length - 1);
  const ys = (v) => H - PAD - ((v - min) * (H - 2 * PAD)) / (100 - min || 1);
  const line = pts.map((p, i) => `${xs(i)},${ys(p.overall_score)}`).join(" ");
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full" data-testid="cert-trend-chart">
      <line x1={PAD} y1={ys(97)} x2={W - PAD} y2={ys(97)} stroke="#3FB950" strokeOpacity="0.25" strokeDasharray="4 4" />
      <line x1={PAD} y1={ys(90)} x2={W - PAD} y2={ys(90)} stroke="#E0A526" strokeOpacity="0.25" strokeDasharray="4 4" />
      <polyline points={line} fill="none" stroke="#9FA8D4" strokeWidth="1.5" />
      {pts.map((p, i) => (
        <circle key={p.id || i} cx={xs(i)} cy={ys(p.overall_score)} r="3"
                fill={HEALTH_COLOR[p.health] || "#9FA8D4"}>
          <title>{`${_fmt(p.at)} — ${p.overall_score}% (${p.health})`}</title>
        </circle>
      ))}
    </svg>
  );
};

/* ---------- Failure row with drill-down ---------- */
const FailureRow = ({ f, rec }) => {
  const [open, setOpen] = useState(false);
  const path = (RECORD_PATH[f.type] || RECORD_PATH.dossier)(f.record);
  return (
    <div className="border border-[#2D2A26]" data-testid={`cert-failure-${f.record}-${f.kind}`}>
      <button type="button" onClick={() => setOpen((o) => !o)}
              className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-[#141310] transition-colors"
              data-testid={`cert-failure-toggle-${f.record}-${f.kind}`}>
        {open ? <ChevronDown size={11} className="text-[#7B7168] shrink-0" />
              : <ChevronRight size={11} className="text-[#7B7168] shrink-0" />}
        <span className="font-mono text-[8px] uppercase tracking-[0.2em] px-1.5 py-0.5 shrink-0"
              style={{ background: f.critical ? "#D92D20" : "#454038", color: "#E4DFD3" }}>
          {f.critical ? "CRITICAL" : "MINOR"}
        </span>
        <span className="font-mono text-[8px] uppercase tracking-[0.18em] text-[#7B7168] shrink-0">{f.type}</span>
        <span className="text-[#E4DFD3] text-sm truncate">{f.record_title || f.record}</span>
        <span className="ml-auto font-mono text-[9px] text-[#A39E93] truncate max-w-[200px]">"{f.query}"</span>
      </button>
      {open && (
        <div className="px-3 pb-3 pt-1 border-t border-[#2D2A26] bg-[#12110e]">
          <div className="grid grid-cols-2 gap-x-6 gap-y-1 font-mono text-[10px] text-[#A39E93]">
            <div>Query kind: <span className="text-[#E4DFD3]">{f.kind}</span></div>
            <div>Miss class: <span style={{ color: f.miss_class === "false_positive" ? "#E0A526" : "#D92D20" }}>
              {f.miss_class === "false_positive" ? "FALSE POSITIVE (wrong records surfaced)" : "FALSE NEGATIVE (nothing surfaced)"}</span></div>
            <div>Found: <span className="text-[#E4DFD3]">{String(f.found)}</span></div>
            <div>Rank: <span className="text-[#E4DFD3]">{f.rank ?? "—"}</span></div>
          </div>
          <div className="mt-1.5 font-mono text-[10px] text-[#C9C2B4]">{f.explanation}</div>
          {rec && (
            <div className="mt-2 border-l-2 border-[#E0A526] pl-2 font-mono text-[10px] text-[#E0A526]"
                 data-testid={`cert-recommendation-${f.record}-${f.kind}`}>
              RECOMMENDATION: <span className="text-[#C9C2B4]">{rec}</span>
            </div>
          )}
          <Link to={path} target="_blank"
                data-testid={`cert-view-record-${f.record}`}
                className="mt-2 inline-flex items-center gap-1 px-2 py-1 border border-[#454038] font-mono text-[8px] uppercase tracking-[0.18em] text-[#A39E93] hover:border-[#58A6FF] hover:text-[#58A6FF] transition-colors">
            <ExternalLink size={9} /> View affected record
          </Link>
        </div>
      )}
    </div>
  );
};

export const AdminOracleCertification = ({ token, userToken }) => {
  const [data, setData] = useState(null);
  const [reports, setReports] = useState([]);
  const [range, setRange] = useState(30); // 30 = 30-day, 0 = all-time
  const [loading, setLoading] = useState(true);
  const [rerunning, setRerunning] = useState(false);
  const pollRef = useRef(null);

  const load = useCallback(async (days = range) => {
    try {
      const [latest, hist] = await Promise.all([
        adminCertLatest(token, userToken),
        adminCertReports(token, days, userToken),
      ]);
      setData(latest);
      setReports(hist.reports || []);
      return latest;
    } catch (e) {
      if (e?.response?.status !== 404) toast.error("Failed to load certification data");
      setData((d) => d || { report: null });
      return null;
    } finally { setLoading(false); }
  }, [token, userToken, range]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!data?.running && !rerunning) return undefined;
    pollRef.current = setInterval(async () => {
      const latest = await load();
      if (latest && !latest.running) {
        setRerunning(false);
        clearInterval(pollRef.current);
        toast.success(`Certification complete — ${latest.report?.health || "done"}`);
      }
    }, 5000);
    return () => clearInterval(pollRef.current);
  }, [data?.running, rerunning, load]);

  const rerun = async () => {
    try {
      await adminCertRun(token, userToken);
      setRerunning(true);
      toast.info("Certification run started — grading the full Archive…");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to start run"); }
  };

  const rep = data?.report;
  const running = data?.running || rerunning;
  const recMap = useMemo(() => {
    const m = {};
    (rep?.recommendations || []).forEach((r) => { m[`${r.record}|${r.kind}`] = r.recommendation; });
    return m;
  }, [rep]);
  const criticalFails = (rep?.failures || []).filter((f) => f.critical);
  const minorFails = (rep?.failures || []).filter((f) => !f.critical);

  if (loading) {
    return <div className="flex items-center gap-2 py-10 justify-center text-[#7B7168] font-mono text-xs" data-testid="cert-loading">
      <Loader2 size={14} className="animate-spin" /> LOADING CERTIFICATION…
    </div>;
  }

  return (
    <div className="space-y-5" data-testid="oracle-certification-panel">
      {/* Header */}
      <div className="flex flex-wrap items-center gap-3">
        <ShieldCheck size={16} className="text-[#9FA8D4]" />
        <h2 className="font-display text-lg text-[#E4DFD3] tracking-wide">RETRIEVAL CERTIFICATION</h2>
        {rep && (
          <span className="font-mono text-[9px] uppercase tracking-[0.25em] px-2 py-0.5"
                style={{ background: HEALTH_COLOR[rep.health], color: "#0a0907" }}
                data-testid="cert-health-badge">{rep.health}</span>
        )}
        <span className="font-mono text-[8px] uppercase tracking-[0.2em] text-[#7B7168]"
              data-testid="cert-version">engine v{rep?.version || data?.cert_version}</span>
        <button type="button" onClick={rerun} disabled={running}
                data-testid="cert-rerun-btn"
                className="ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 border border-[#454038] font-mono text-[9px] uppercase tracking-[0.2em] text-[#A39E93] hover:border-[#9FA8D4] hover:text-[#9FA8D4] transition-colors disabled:opacity-40">
          {running ? <Loader2 size={10} className="animate-spin" /> : <RefreshCw size={10} />}
          {running ? "Certifying…" : "Rerun certification"}
        </button>
      </div>

      {data?.last_error && (
        <div className="border border-[#D92D20] px-3 py-2 font-mono text-[10px] text-[#D92D20]" data-testid="cert-last-error">
          <AlertTriangle size={10} className="inline mr-1.5" />
          Last internal error ({_fmt(data.last_error.at)}): {data.last_error.error} — other nightly jobs were unaffected.
        </div>
      )}

      {!rep ? (
        <div className="border border-[#2D2A26] px-4 py-8 text-center font-mono text-[11px] text-[#7B7168]" data-testid="cert-no-report">
          No certification report yet. Run the first certification to grade the Archive.
        </div>
      ) : (
        <>
          {/* Timestamps */}
          <div className="flex flex-wrap gap-x-6 gap-y-1 font-mono text-[9px] uppercase tracking-[0.15em] text-[#7B7168]">
            <span data-testid="cert-last-run"><Clock size={9} className="inline mr-1" />Last run: <span className="text-[#A39E93]">{_fmt(rep.at)}</span> ({rep.trigger}, {rep.duration_seconds}s)</span>
            <span data-testid="cert-last-successful">Last successful (PASS): <span className="text-[#3FB950]">{data?.last_successful ? `${_fmt(data.last_successful.at)} — ${data.last_successful.overall_score}%` : "never"}</span></span>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2">
            <Chip label="Health Score" value={`${rep.overall_score}%`} color={HEALTH_COLOR[rep.health]} testid="cert-stat-score" />
            <Chip label="Records Audited" value={rep.records_audited ?? "—"} testid="cert-stat-records" />
            <Chip label="Queries Tested" value={rep.total_queries} testid="cert-stat-queries" />
            <Chip label="Success Rate" value={`${rep.query_success_rate ?? "—"}%`} testid="cert-stat-success" />
            <Chip label="False Positives" value={rep.false_positives ?? "—"} color={rep.false_positives ? "#E0A526" : "#3FB950"} testid="cert-stat-fp" />
            <Chip label="False Negatives" value={rep.false_negatives ?? "—"} color={rep.false_negatives ? "#D92D20" : "#3FB950"} testid="cert-stat-fn" />
            <Chip label="Avg Retrieval" value={rep.avg_retrieval_ms ? `${rep.avg_retrieval_ms}ms` : "—"} testid="cert-stat-latency" />
          </div>

          {/* Comparison vs previous run */}
          {rep.delta && (
            <div className="border border-[#2D2A26] px-3 py-2.5" data-testid="cert-comparison">
              <div className="font-mono text-[8px] uppercase tracking-[0.25em] text-[#7B7168] mb-1.5">
                VS PREVIOUS RUN — {_fmt(rep.delta.previous_at)} ({rep.delta.previous_health}, {rep.delta.previous_score}%)
              </div>
              <div className="flex flex-wrap gap-x-6 gap-y-1 items-center font-mono text-[10px] text-[#A39E93]">
                <span>Score: <Delta value={rep.delta.score} suffix=" pts" /></span>
                <span>Failures: <Delta value={rep.delta.failures} invert /></span>
                <span>New failures: <span style={{ color: rep.delta.new_failures ? "#D92D20" : "#3FB950" }}>{rep.delta.new_failures}</span></span>
                <span>Resolved: <span style={{ color: rep.delta.resolved_failures ? "#3FB950" : "#7B7168" }}>{rep.delta.resolved_failures}</span></span>
                {rep.significant_regression && (
                  <span className="px-1.5 py-0.5 font-mono text-[8px] uppercase tracking-[0.2em]" style={{ background: "#D92D20", color: "#0a0907" }} data-testid="cert-significant-regression">SIGNIFICANT REGRESSION</span>
                )}
              </div>
            </div>
          )}

          {/* Benchmarks */}
          <div className="border border-[#2D2A26]" data-testid="cert-benchmarks">
            <div className="px-3 py-2 border-b border-[#2D2A26] flex items-center gap-2">
              <Crosshair size={11} className="text-[#9FA8D4]" />
              <span className="font-mono text-[8px] uppercase tracking-[0.25em] text-[#7B7168]">BENCHMARK QUERIES — MUST NEVER REGRESS</span>
              {(rep.benchmark_regressions || []).length > 0 && (
                <span className="ml-auto font-mono text-[8px] uppercase tracking-[0.2em] px-1.5 py-0.5" style={{ background: "#D92D20", color: "#0a0907" }} data-testid="cert-benchmark-regression-badge">
                  {rep.benchmark_regressions.length} REGRESSED
                </span>
              )}
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-px bg-[#2D2A26]">
              {(rep.sentinels || []).map((s) => {
                const regressed = (rep.benchmark_regressions || []).includes(s.query);
                return (
                  <div key={s.query} className="bg-[#0F0E0B] px-3 py-2 flex items-center gap-2"
                       style={regressed ? { outline: "1px solid #D92D20", outlineOffset: "-1px" } : undefined}
                       title={s.explanation}
                       data-testid={`cert-benchmark-${s.query.toLowerCase().replace(/\s+/g, "-")}`}>
                    {s.skipped ? <Minus size={11} className="text-[#7B7168] shrink-0" />
                      : s.ok ? <CheckCircle2 size={11} className="text-[#3FB950] shrink-0" />
                      : <XCircle size={11} className="text-[#D92D20] shrink-0" />}
                    <span className={`font-mono text-[10px] truncate ${s.skipped ? "text-[#7B7168]" : "text-[#E4DFD3]"}`}>{s.query}</span>
                    {s.skipped && <span className="ml-auto font-mono text-[7px] uppercase tracking-[0.15em] text-[#7B7168]">DORMANT</span>}
                    {regressed && <span className="ml-auto font-mono text-[7px] uppercase tracking-[0.15em] text-[#D92D20]">REGRESSION</span>}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Trend */}
          <div className="border border-[#2D2A26]" data-testid="cert-trend">
            <div className="px-3 py-2 border-b border-[#2D2A26] flex items-center gap-2">
              <span className="font-mono text-[8px] uppercase tracking-[0.25em] text-[#7B7168]">RETRIEVAL HEALTH TREND</span>
              <div className="ml-auto flex gap-1">
                {[[30, "30 DAYS"], [0, "ALL TIME"]].map(([d, label]) => (
                  <button key={label} type="button" onClick={() => { setRange(d); load(d); }}
                          data-testid={`cert-trend-range-${d}`}
                          className={`px-2 py-0.5 border font-mono text-[8px] uppercase tracking-[0.2em] transition-colors ${range === d ? "border-[#9FA8D4] text-[#9FA8D4]" : "border-[#2D2A26] text-[#7B7168] hover:text-[#A39E93]"}`}>
                    {label}
                  </button>
                ))}
              </div>
            </div>
            <div className="px-3 py-2">
              <TrendChart reports={reports} />
              <div className="flex gap-4 font-mono text-[8px] uppercase tracking-[0.15em] text-[#7B7168]">
                <span><span className="text-[#3FB950]">▬</span> PASS ≥97%</span>
                <span><span className="text-[#E0A526]">▬</span> WARNING ≥90%</span>
                <span>{reports.filter((r) => r.health !== "ERROR").length} runs shown</span>
              </div>
            </div>
          </div>

          {/* Failure drill-down */}
          <div data-testid="cert-failures">
            <div className="font-mono text-[8px] uppercase tracking-[0.25em] text-[#7B7168] mb-2">
              FAILURE DRILL-DOWN — {criticalFails.length} CRITICAL · {minorFails.length} MINOR
            </div>
            {(rep.failures || []).length === 0 ? (
              <div className="border border-[#2D2A26] px-3 py-4 text-center font-mono text-[10px] text-[#3FB950]" data-testid="cert-no-failures">
                <CheckCircle2 size={12} className="inline mr-1.5" />
                Every certified query retrieved its record. No failures.
              </div>
            ) : (
              <div className="space-y-1.5">
                {[...criticalFails, ...minorFails].map((f, i) => (
                  <FailureRow key={`${f.record}-${f.kind}-${i}`} f={f}
                              rec={recMap[`${f.record}|${f.kind}`]} />
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default AdminOracleCertification;
